import { z } from "zod";
import { sqlNumberSchema } from "./common.js";
/**
 * Universal search: `GET /api/networks/:id/search?q=...&limit=...`.
 *
 * The query is trimmed before validation so a whitespace-only `q` fails the
 * min(1) check rather than reaching the probes. The 200-char ceiling keeps
 * label ILIKE patterns bounded.
 *
 * `limit` caps every match list (the quick-jump dropdown takes the default of
 * 10, the results page asks for more). It stays a string here, as querystring
 * numbers do throughout this API — the route parses and clamps it to [1, 100].
 */
export const searchQuerySchema = z.object({
    q: z.string().trim().min(1).max(200),
    limit: z.string().optional(),
});
/**
 * How the server classified the normalized query, which decides which probe
 * groups run: `fieldHex` = 0x + 64 hex (Fr/AztecAddress/hash), `l1Address` =
 * 0x + 40 hex, `selector` = 0x + 8 hex, `numeric` = a bare integer, `label` =
 * free text matched against contract labels.
 */
export const searchQueryTypeSchema = z.enum([
    "fieldHex",
    "l1Address",
    "selector",
    "numeric",
    "label",
]);
/**
 * A tx-scoped hit with the position of the matching element within that tx
 * (nullifier index, note hash index, log index).
 */
export const searchTxMatchSchema = z.object({
    txHash: z.string(),
    position: sqlNumberSchema,
});
/**
 * One role an address was seen in, with how many txs it appeared in.
 * `capped` is true when the count hit the LIMIT 1000 probe ceiling — the UI
 * renders those as "1000+". `l2ToL1Recipient` is deliberately absent: it is
 * never emitted onto public_address_appearances (L1 recipients live only on
 * l2_to_l1_messages.recipient), and `calldata` gets its own interpretation
 * kind because it is dominated by constants.
 */
export const searchAddressRoleSchema = z.object({
    role: z.enum([
        "feePayer",
        "msgSender",
        "callee",
        "l2ToL1Sender",
        "logEmitter",
        "classLogEmitter",
    ]),
    txCount: sqlNumberSchema,
    capped: z.boolean(),
});
/**
 * One thing the query could mean. A single query commonly yields several — the
 * same 64-hex field can be a tx hash, a nullifier and a calldata constant — so
 * the endpoint returns every non-empty probe result rather than guessing.
 *
 * Counts come from `count(*)` over a `LIMIT 1000` subquery and so arrive from
 * postgres.js as strings; every numeric field here goes through
 * sqlNumberSchema, which normalizes both drivers to `number`.
 */
export const searchInterpretationSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("tx"),
        txHash: z.string(),
        status: z.string(),
        blockNumber: sqlNumberSchema.nullable(),
        feePayer: z.string(),
    }),
    z.object({
        kind: z.literal("block"),
        blockNumber: sqlNumberSchema,
        blockHash: z.string().nullable(),
        matchedBy: z.enum(["number", "hash"]),
        numTxs: sqlNumberSchema,
        timestamp: sqlNumberSchema.nullable(),
    }),
    z.object({
        kind: z.literal("address"),
        address: z.string(),
        label: z.string().nullable(),
        roles: z.array(searchAddressRoleSchema),
        latestTxHash: z.string().nullable(),
    }),
    z.object({
        kind: z.literal("calldataValue"),
        value: z.string(),
        txCount: sqlNumberSchema,
        capped: z.boolean(),
    }),
    z.object({
        kind: z.literal("nullifier"),
        value: z.string(),
        matches: z.array(searchTxMatchSchema),
    }),
    z.object({
        kind: z.literal("noteHash"),
        value: z.string(),
        matches: z.array(searchTxMatchSchema),
    }),
    z.object({
        kind: z.literal("logField"),
        scope: z.enum(["private", "public", "contractClass"]),
        matches: z.array(searchTxMatchSchema),
        capped: z.boolean(),
    }),
    z.object({
        kind: z.literal("l2ToL1Message"),
        matchedBy: z.enum(["content", "sender", "messageHash", "recipient"]),
        matches: z.array(z.object({
            txHash: z.string(),
            recipient: z.string().nullable(),
            sender: z.string().nullable(),
            content: z.string().nullable(),
        })),
        capped: z.boolean(),
    }),
    z.object({
        kind: z.literal("publicDataWrite"),
        matchedBy: z.enum(["leafSlot", "value"]),
        matches: z.array(z.object({
            txHash: z.string(),
            leafSlot: z.string(),
        })),
        capped: z.boolean(),
    }),
    z.object({
        kind: z.literal("contractClass"),
        contractClassId: z.string(),
        matches: z.array(z.object({
            txHash: z.string(),
            contractAddress: z.string(),
        })),
    }),
    // `blockCount` is the capped total; `blockNumbers` is the newest-first page of
    // it bounded by the request limit, and `latestBlockNumber` is its first entry.
    z.object({
        kind: z.literal("blockProducer"),
        address: z.string(),
        role: z.enum(["coinbase", "feeRecipient"]),
        blockCount: sqlNumberSchema,
        capped: z.boolean(),
        blockNumbers: z.array(sqlNumberSchema),
        latestBlockNumber: sqlNumberSchema.nullable(),
    }),
    z.object({
        kind: z.literal("validator"),
        address: z.string(),
        isValidator: z.boolean(),
        proposedSlotCount: sqlNumberSchema,
        capped: z.boolean(),
        latestProposedSlot: sqlNumberSchema.nullable(),
    }),
    z.object({
        kind: z.literal("slot"),
        slotNumber: sqlNumberSchema,
        epoch: sqlNumberSchema,
    }),
    z.object({
        kind: z.literal("epoch"),
        epoch: sqlNumberSchema,
        isCurrent: z.boolean(),
    }),
    z.object({
        kind: z.literal("checkpoint"),
        checkpointNumber: sqlNumberSchema,
        blockCount: sqlNumberSchema.nullable(),
    }),
    z.object({
        kind: z.literal("label"),
        label: z.string(),
        address: z.string(),
        contractType: z.string().nullable(),
    }),
    // Same split as blockProducer: capped total, newest-first page of tx hashes,
    // and `latestTxHash` as its first entry.
    z.object({
        kind: z.literal("functionSelector"),
        selector: z.string(),
        txCount: sqlNumberSchema,
        capped: z.boolean(),
        matches: z.array(z.object({ txHash: z.string() })),
        latestTxHash: z.string().nullable(),
    }),
    z.object({
        kind: z.literal("rollupContract"),
        address: z.string(),
    }),
]);
/**
 * `interpretations` is ranked by the server (most specific first: tx, block,
 * address, label, ... calldataValue last) and may legitimately be empty — that
 * is a 200 with no results, not an error.
 */
export const searchResponseSchema = z.object({
    query: z.string(),
    normalizedQuery: z.string(),
    queryType: searchQueryTypeSchema,
    interpretations: z.array(searchInterpretationSchema),
});
//# sourceMappingURL=search.js.map